import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';

import { ApiError } from '../../../api/client';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { Input } from '../../../shared/ui/Input';
import { Spinner } from '../../../shared/ui/Spinner';
import { useMfaEnable } from '../hooks/useMfaEnable';
import { useMfaSetup } from '../hooks/useMfaSetup';
import styles from './EnableMfaModal.module.css';

interface EnableMfaModalProps {
  onClose: () => void;
}

// PARA-151, AC-1/AC-2/AC-E1/AC-E5: enrollment своей MFA — при открытии
// запрашивает setup (otpauth_uri + секрет), рендерит QR + секрет для ручного
// ввода, подтверждает первым 6-значным кодом. 400 — неверный код (остаёмся,
// повторный ввод), 429 — rate-limit, успех — закрытие (список инвалидирует хук).
export function EnableMfaModal({ onClose }: EnableMfaModalProps): JSX.Element {
  const { t } = useTranslation();
  const setup = useMfaSetup();
  const enable = useMfaEnable();
  const [totpCode, setTotpCode] = useState('');

  const { mutate: startSetup } = setup;
  useEffect(() => {
    startSetup();
  }, [startSetup]);

  const submit = (): void => {
    enable.mutate(totpCode, { onSuccess: onClose });
  };

  const enableStatus = enable.error instanceof ApiError ? enable.error.status : null;
  const enableErrorKey =
    enableStatus === 400
      ? 'admins.mfa.wrongCode'
      : enableStatus === 429
        ? 'admins.mfa.rateLimited'
        : 'admins.mfa.error';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enable-mfa-title"
      className={styles.dialogOverlay}
    >
      <div className={styles.dialogContent}>
        <h3 id="enable-mfa-title">{t('admins.mfa.enableTitle')}</h3>
        {setup.isPending && <Spinner />}
        {setup.isError && (
          <p className={styles.error} role="alert">
            {t('admins.mfa.setupError')}
          </p>
        )}
        {setup.isSuccess && (
          <>
            <p className={styles.dialogField}>{t('admins.mfa.enableDescription')}</p>
            <div className={styles.qr}>
              <QRCodeSVG value={setup.data.otpauth_uri} size={192} />
            </div>
            <p className={styles.secret}>
              {t('admins.mfa.manualSecret')}: {setup.data.secret}
            </p>
            <Field
              label={t('admins.mfa.codeLabel')}
              htmlFor="enable-mfa-code"
              className={styles.dialogField}
            >
              <Input
                id="enable-mfa-code"
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Field>
          </>
        )}
        {enable.isError && (
          <p className={styles.error} role="alert">
            {t(enableErrorKey)}
          </p>
        )}
        <div className={styles.dialogActions}>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={enable.isPending}
            className={styles.dialogActionButton}
          >
            {t('admins.mfa.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!setup.isSuccess || enable.isPending}
          >
            {t('admins.mfa.enableSubmit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
